import { overrideInForce, readHoursField, type HoursSchedule } from "@starsach/shared-types";

export interface HoursNow {
  open: boolean;
  reason: "open" | "closed" | "holiday" | "override";
  holiday?: string;
  /** The override in force is an emergency close (an empty week), not different hours. */
  emergency?: boolean;
  announcement?: string;
}

/** An override on an hours field, as the profile's detail lists it. */
export interface HoursOverride {
  startsAt: string;
  expiresAt?: string | undefined;
  value: unknown;
}

const isSchedule = (v: unknown): v is HoursSchedule => Boolean(v && typeof v === "object" && "weekly" in v && Array.isArray((v as { weekly: unknown }).weekly));

/**
 * The same decision the lander makes at call time, for the screen's
 * "right now" line - not authoritative, and never sent anywhere. An
 * override in force (an emergency close) replaces the schedule, then a
 * holiday window closes it, then the week decides (#484); the reading
 * itself is shared/types `readHoursField`, so the two cannot disagree.
 */
export function hoursNow(schedule: HoursSchedule, now: Date = new Date(), override?: HoursOverride): HoursNow {
  const overridden = Boolean(override && isSchedule(override.value) && overrideInForce(override.startsAt, override.expiresAt, now));
  const effective = overridden ? (override!.value as HoursSchedule) : schedule;
  const reading = readHoursField(effective, now, overridden);
  if (reading.open) return { open: true, reason: "open" };
  const announcement = reading.holiday?.announcement ?? effective.closed_announcement;
  return {
    open: false,
    reason: reading.reason,
    ...(reading.holiday ? { holiday: reading.holiday.name } : {}),
    ...(overridden && effective.weekly.length === 0 ? { emergency: true } : {}),
    ...(announcement ? { announcement } : {}),
  };
}

/** "open", "closed", or closed with its reason named: `closed (emergency close)`, `closed (holiday: Christmas)`. */
export function describeHoursNow(now: HoursNow): string {
  if (now.open) return "open";
  if (now.reason === "override") return now.emergency ? "closed (emergency close)" : "closed (override)";
  if (now.reason === "holiday") return `closed (holiday: ${now.holiday})`;
  return "closed";
}
